/** Riga di vendita del portale: i kWh restano stringa fino al database. */
export interface RigaVendita {
  readonly mese: number
  readonly provincia: string
  readonly tipologia: string
  readonly numeroUtenze: number
  readonly kwh: string
}

export interface EsitoInterpretazione {
  readonly righe: RigaVendita[]
  readonly errori: string[]
}

const COLONNE = ['Mese', 'Provincia', 'Tipologia', 'Numero utenze', 'kWh'] as const

/** Stessi codici del vincolo di vendita.tipologia (0034_vendita.sql). */
export const TIPOLOGIA_VENDITA = /^(J[1-7]|L[1-9]|M([1-9]|1[0-2])|M4\+M5\+M6\+M7)$/

/** «1.234,5» (testo italiano) -> «1234.5»; «1234.5» (cella numerica) resta com'e'. */
export function normalizzaKwh(v: string): string {
  const t = v.trim()
  return t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t
}

export function interpretaVendite(
  foglio: readonly (readonly string[])[],
  opzioni: {
    readonly mesi: readonly number[]
    readonly province: ReadonlySet<string>
    /** Numero con cui citare la prima riga di dati: 2 nel foglio (sotto
     * l'intestazione), 1 nella griglia del portale. */
    readonly primaRiga?: number
  },
): EsitoInterpretazione {
  const intestazione = (foglio[0] ?? []).map((c) => c.trim().toLowerCase())
  const indice = Object.fromEntries(COLONNE.map((c) => [c, intestazione.indexOf(c.toLowerCase())])) as Record<
    (typeof COLONNE)[number],
    number
  >
  const mancanti = COLONNE.filter((c) => indice[c] < 0)
  if (mancanti.length > 0) return { righe: [], errori: [`intestazione: mancano le colonne ${mancanti.join(', ')}`] }

  const righe: RigaVendita[] = []
  const errori: string[] = []
  const viste = new Map<string, number>()

  foglio.slice(1).forEach((cellule, i) => {
    const numero = i + (opzioni.primaRiga ?? 2)
    const cella = (c: (typeof COLONNE)[number]) => (cellule[indice[c]] ?? '').trim()
    if (COLONNE.every((c) => cella(c) === '')) return

    const mese = Number(cella('Mese'))
    const provincia = cella('Provincia').toUpperCase()
    const tipologia = cella('Tipologia').toUpperCase().replace(/\s/g, '')
    const utenze = cella('Numero utenze')
    const kwh = normalizzaKwh(cella('kWh'))

    if (!opzioni.mesi.includes(mese)) errori.push(`riga ${numero}: mese ${cella('Mese')} fuori dal periodo`)
    else if (!opzioni.province.has(provincia)) errori.push(`riga ${numero}: provincia «${cella('Provincia')}» sconosciuta`)
    else if (!TIPOLOGIA_VENDITA.test(tipologia)) errori.push(`riga ${numero}: tipologia «${cella('Tipologia')}» non ammessa`)
    else if (!/^\d+$/.test(utenze)) errori.push(`riga ${numero}: numero utenze «${utenze}» non valido`)
    else if (!/^\d+(\.\d+)?$/.test(kwh)) errori.push(`riga ${numero}: kWh «${cella('kWh')}» non validi`)
    else {
      const chiave = `${mese}|${provincia}|${tipologia}`
      const gia = viste.get(chiave)
      if (gia !== undefined) {
        errori.push(`riga ${numero}: mese ${mese}, ${provincia}, ${tipologia} gia presente alla riga ${gia}`)
        return
      }
      viste.set(chiave, numero)
      righe.push({ mese, provincia, tipologia, numeroUtenze: Number(utenze), kwh })
    }
  })
  return { righe, errori }
}
